import jsPDF from 'jspdf';

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/lux_logo.PNG';
    } catch { resolve(null); }
  });
}

function fmt(amount) {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
}

function fmtDate(dateStr) {
  if (!dateStr) return '-';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  try {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return dateStr;
  } catch { return dateStr; }
}

const FORMA_PAGO_LABELS = {
  PAGO_EN_LINEA: 'En línea',
  TARJETA_DEBITO: 'T. Débito',
  TARJETA_CREDITO: 'T. Crédito',
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA: 'Transferencia',
  DEPOSITO: 'Depósito',
  CONTADO: 'Contado',
  PARCIALIDADES: 'Parcialidades',
};

// ─── Main Export ──────────────────────────────────────────────────────────────

/**
 * Genera y descarga el PDF del Estado de Cuenta del estudiante.
 *
 * @param {object} options
 * @param {object}   options.student           - Datos del estudiante
 * @param {Array}    options.pagosPorCiclo     - Estructura ya agrupada por ciclo/folio (pagos realizados)
 * @param {Array}    options.adeudosPendientes - Array de adeudos pendientes
 * @param {object}   options.resumen           - Resumen financiero (saldo_total, saldo_vencido, …)
 * @param {boolean}  [options.descargar]       - true para forzar descarga automática
 * @param {string}   [options.folio]           - Folio opcional de control
 * @returns {string} Data URI del PDF generado
 */
export async function generateEstadoCuentaPDF({ student = {}, pagosPorCiclo = [], adeudosPendientes = [], resumen = {}, descargar = true, folio = "" }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth  = doc.internal.pageSize.getWidth();   // 210
  const pageHeight = doc.internal.pageSize.getHeight();  // 297
  const margin = 14;
  const cW = pageWidth - margin * 2; // 182

  const logoDataUrl = await loadLogoDataUrl();

  // ─── ENCABEZADO ──────────────────────────────────────────────────────────
  const logoSize = 18;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', margin, 10, logoSize, logoSize);
  }

  const txX = margin + logoSize + 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Centro De Estudios Superiores CMP, A.C.', txX, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Ave. Venustiano Carranza Nte. 614 | CENTRO | MONTERREY, N.L. C.P. 64000', txX, 20);
  doc.text('R.F.C: CES9912132F4', txX, 25);

  // Título derecho
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 133, 124);
  doc.text('ESTADO DE CUENTA', pageWidth - margin, 16, { align: 'right' });

  // Folio del recibo/reporte cerca del título (debajo de ESTADO DE CUENTA)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const folioFinal = folio || student.folio || student.folio_recibo || `EC-${student.matricula || student.numero_cuenta || '000000'}`;
  doc.text(`FOLIO: ${folioFinal}`, pageWidth - margin, 22, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // Línea separadora
  const lineY = 31;
  doc.setDrawColor(0, 133, 124);
  doc.setLineWidth(0.5);
  doc.line(margin, lineY, pageWidth - margin, lineY);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);

  // ─── INFORMACIÓN DEL ESTUDIANTE ──────────────────────────────────────────
  const infoY   = lineY + 4;
  const infoH   = 32;
  const halfW   = cW / 2;
  const rowGap  = 6.5;

  doc.setFillColor(247, 250, 250);
  doc.rect(margin, infoY, cW, infoH, 'F');
  doc.setDrawColor(0, 133, 124);
  doc.rect(margin, infoY, cW, infoH);
  doc.setDrawColor(0, 0, 0);

  const studentName =
    student.nombre_completo ||
    student.nombre ||
    [student.primer_nombre, student.segundo_nombre, student.primer_apellido, student.segundo_apellido]
      .filter(Boolean).join(' ') ||
    '-';

  const cols = [
    // [label, value, column (1|2), row]
    ['Matrícula:',  student.matricula   || student.numero_cuenta || '-',                          1, 0],
    ['Nombre:',     studentName,                                                                   1, 1],
    ['Programa:',   student.programa_nombre || student.programa || '-',                           1, 2],
    ['Turno:',      student.turno || '-',                                                         1, 3],
    ['Campus:',     student.campus_nombre || student.campus || '-',                               2, 0],
    ['Ciclo:',      student.ciclo_escolar_nombre || student.ciclo_escolar || '-',                 2, 1],
    ['Modalidad:',  student.modalidad || '-',                                                     2, 2],
    ['Estatus:',    student.estatus_estudiante || student.estatus || student.estado || '-',       2, 3],
  ];

  const labelW = 24;
  doc.setFontSize(7.5);

  cols.forEach(([label, value, col, row]) => {
    const bx  = col === 1 ? margin + 3          : margin + halfW + 3;
    const vx  = col === 1 ? margin + labelW + 3 : margin + halfW + labelW + 3;
    const y   = infoY + 7 + row * rowGap;
    const maxW = halfW - labelW - 6;

    doc.setFont('helvetica', 'bold');
    doc.text(label, bx, y);
    doc.setFont('helvetica', 'normal');
    const truncated = doc.splitTextToSize(String(value), maxW)[0] || '';
    doc.text(truncated, vx, y);
  });

  // Fecha de generación
  const now      = new Date();
  const fechaHoy = now.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City' });
  const horaHoy  = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(120, 120, 120);
  doc.text(`Generado el ${fechaHoy} a las ${horaHoy}`, pageWidth - margin, infoY + infoH + 4, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // ─── HELPERS PARA TABLAS ─────────────────────────────────────────────────
  const tHdrH  = 6.5;
  const rowH   = 5.5;
  const footerZone = 20; // espacio reservado al pie

  const drawTableHeader = (y, labels, columnWidths, columnAligns) => {
    doc.setFillColor(0, 133, 124);
    const totalW = columnWidths.reduce((a, b) => a + b, 0);
    doc.rect(margin, y, totalW, tHdrH, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    
    let xPos = margin;
    labels.forEach((lbl, i) => {
      const textY = y + tHdrH - 1.8;
      const colW = columnWidths[i];
      if (columnAligns[i] === 'center') doc.text(lbl, xPos + colW / 2, textY, { align: 'center' });
      else if (columnAligns[i] === 'right') doc.text(lbl, xPos + colW - 2, textY, { align: 'right' });
      else doc.text(lbl, xPos + 2, textY);
      xPos += colW;
    });
    doc.setTextColor(0, 0, 0);
    return y + tHdrH;
  };

  const checkPageBreak = (curY, needed) => {
    if (curY + needed > pageHeight - footerZone) {
      doc.addPage();
      return 15;
    }
    return curY;
  };

  // ─── SECCIÓN 1: PAGOS REALIZADOS ─────────────────────────────────────────
  let curY = infoY + infoH + 8;

  // Título de sección
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 133, 124);
  doc.text('PAGOS REALIZADOS', margin, curY);
  curY += 6;

  // Columnas para pagos: Ciclo | Fecha | Concepto | Descripción | Monto Original | Desc. Beca | Recargo | Total | Estado
  const colWPagos   = [18, 16, 18, 44, 18, 16, 16, 18, 18]; // suma = 182
  const colLblPagos = ['CICLO', 'FECHA', 'CONCEPTO', 'DESCRIPCIÓN', 'MONTO ORIG.', 'DESC. BECA', 'RECARGO', 'TOTAL', 'ESTADO'];
  const colAlnPagos = ['left', 'center', 'center', 'left', 'right', 'right', 'right', 'right', 'center'];

  const colXPagos = [];
  let cxPagos = margin;
  colWPagos.forEach(w => { colXPagos.push(cxPagos); cxPagos += w; });

  curY = drawTableHeader(curY, colLblPagos, colWPagos, colAlnPagos);

  if (!pagosPorCiclo || pagosPorCiclo.length === 0) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('No hay pagos realizados.', margin + colWPagos.reduce((a, b) => a + b, 0) / 2, curY + 6, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    curY += 10;
  } else {
    let isEven = false;

    pagosPorCiclo.forEach((ciclo) => {
      ciclo.gruposFolio.forEach((grupo) => {
        grupo.pagos.forEach((pago, pagoIdx) => {
          curY = checkPageBreak(curY, rowH);

          if (isEven) {
            doc.setFillColor(245, 248, 248);
            doc.rect(margin, curY, colWPagos.reduce((a, b) => a + b, 0), rowH, 'F');
          }
          isEven = !isEven;

          const estadoPago  = pago.estado_pago || pago.estado || 'APLICADO';
          const esCancelado = estadoPago === 'CANCELADO';
          const montoOriginal = parseFloat(pago.monto_original || 0);
          const montoAplicado = parseFloat(pago.monto_aplicado || pago.monto || 0);
          const descBeca = montoOriginal > montoAplicado ? (montoOriginal - montoAplicado) : 0;
          const recargo     = parseFloat(pago.monto_recargo || 0);
          const montoTotal  = montoAplicado + recargo;

          const cicloNombreRow = pagoIdx === 0 ? (ciclo.ciclo_nombre || '-') : '';

          const vals = [
            cicloNombreRow,
            fmtDate(pago.fecha || pago.fecha_pago),
            pago.codigo_concepto || pago.codigo || '-',
            pago.descripcion_concepto || pago.descripcion || '-',
            fmt(montoOriginal),
            descBeca > 0 ? fmt(descBeca) : '-',
            fmt(recargo),
            fmt(montoTotal),
            estadoPago,
          ];

          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          const textY = curY + rowH - 1.5;

          vals.forEach((val, i) => {
            const truncated = doc.splitTextToSize(String(val), colWPagos[i] - 3)[0] || '';
            if (i === 8 && esCancelado) doc.setTextColor(190, 20, 20);
            else doc.setTextColor(0, 0, 0);

            if (colAlnPagos[i] === 'center') doc.text(truncated, colXPagos[i] + colWPagos[i] / 2, textY, { align: 'center' });
            else if (colAlnPagos[i] === 'right') doc.text(truncated, colXPagos[i] + colWPagos[i] - 2, textY, { align: 'right' });
            else doc.text(truncated, colXPagos[i] + 2, textY);
          });

          doc.setTextColor(0, 0, 0);
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, curY + rowH, margin + colWPagos.reduce((a, b) => a + b, 0), curY + rowH);
          doc.setDrawColor(0, 0, 0);

          curY += rowH;
        });
      });
    });
  }

  // Borde exterior de tabla de pagos
  doc.setDrawColor(180, 180, 180);
  doc.rect(margin, infoY + infoH + 14, colWPagos.reduce((a, b) => a + b, 0), curY - (infoY + infoH + 14));
  doc.setDrawColor(0, 0, 0);

  curY += 6;

  // ─── SECCIÓN 2: ADEUDOS PENDIENTES ──────────────────────────────────────
  // Título de sección
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 133, 124);
  doc.text('ADEUDOS PENDIENTES', margin, curY);
  curY += 6;

  // Columnas para adeudos: Ciclo | Vencimiento | Concepto | Descripción | Monto Original | Desc. Beca | Monto Neto | Recargo | Saldo
  // Optimización de anchos de columna: el ciclo ocupa 12mm en lugar de 20mm; el concepto de cobro 15mm en lugar de 20mm.
  // El espacio libre de 13mm se asigna a la descripción (subiendo de 40mm a 57mm) para mostrar el concepto completo.
  const colWAdeudos   = [12, 18, 15, 57, 16, 15, 16, 16, 17]; // suma = 182 (alineado con el ancho disponible de la página)
  const colLblAdeudos = ['CICLO', 'VENCIMIENTO', 'CONCEPTO', 'DESCRIPCIÓN', 'MONTO ORIG.', 'DESC. BECA', 'MONTO NETO', 'RECARGO', 'SALDO'];
  const colAlnAdeudos = ['left', 'center', 'center', 'left', 'right', 'right', 'right', 'right', 'right'];

  const colXAdeudos = [];
  let cxAdeudos = margin;
  colWAdeudos.forEach(w => { colXAdeudos.push(cxAdeudos); cxAdeudos += w; });

  const adeudosTableStartY = curY;
  curY = drawTableHeader(curY, colLblAdeudos, colWAdeudos, colAlnAdeudos);

  if (!adeudosPendientes || adeudosPendientes.length === 0) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('No hay adeudos pendientes. ✓', margin + colWAdeudos.reduce((a, b) => a + b, 0) / 2, curY + 6, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    curY += 10;
  } else {
    let isEvenAdeudos = false;

    adeudosPendientes.forEach((adeudo) => {
      curY = checkPageBreak(curY, rowH);

      if (isEvenAdeudos) {
        doc.setFillColor(245, 248, 248);
        doc.rect(margin, curY, colWAdeudos.reduce((a, b) => a + b, 0), rowH, 'F');
      }
      isEvenAdeudos = !isEvenAdeudos;

      const montoOrig   = parseFloat(adeudo.monto_original || adeudo.monto || 0);
      const descBeca    = parseFloat(adeudo.descuento_beca_monto || 0);
      const montoNeto   = parseFloat(adeudo.monto_neto || montoOrig - descBeca);
      const recargo     = parseFloat(adeudo.monto_recargo || 0);
      // Se le suma el recargo al monto neto para obtener el saldo total de la última columna
      const saldo       = (parseFloat(adeudo.saldo_pendiente || 0) || montoNeto) + recargo;

      const cicloNombre = adeudo.ciclo_nombre || (adeudo.id_ciclo_escolar ? `Ciclo ${adeudo.id_ciclo_escolar}` : '-');

      const vals = [
        cicloNombre,
        fmtDate(adeudo.fecha_vencimiento),
        adeudo.codigo || adeudo.codigo_concepto || '-',
        adeudo.concepto || adeudo.descripcion || '-',
        fmt(montoOrig),
        descBeca > 0 ? fmt(descBeca) : '-',
        fmt(montoNeto),
        fmt(recargo),
        fmt(saldo),
      ];

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const textY = curY + rowH - 1.5;

      vals.forEach((val, i) => {
        const truncated = doc.splitTextToSize(String(val), colWAdeudos[i] - 3)[0] || '';
        if (i === 8 && saldo > 0) doc.setTextColor(220, 20, 20); // Resaltar saldo en rojo
        else doc.setTextColor(0, 0, 0);

        if (colAlnAdeudos[i] === 'center') doc.text(truncated, colXAdeudos[i] + colWAdeudos[i] / 2, textY, { align: 'center' });
        else if (colAlnAdeudos[i] === 'right') doc.text(truncated, colXAdeudos[i] + colWAdeudos[i] - 2, textY, { align: 'right' });
        else doc.text(truncated, colXAdeudos[i] + 2, textY);
      });

      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, curY + rowH, margin + colWAdeudos.reduce((a, b) => a + b, 0), curY + rowH);
      doc.setDrawColor(0, 0, 0);

      curY += rowH;
    });
  }

  // Borde exterior de tabla de adeudos
  doc.setDrawColor(180, 180, 180);
  doc.rect(margin, adeudosTableStartY, colWAdeudos.reduce((a, b) => a + b, 0), curY - adeudosTableStartY);
  doc.setDrawColor(0, 0, 0);

  // ─── RESUMEN FINANCIERO ───────────────────────────────────────────────────
  curY = checkPageBreak(curY, 26);
  curY += 4;
  const resW   = 88;
  const resH   = 22;
  const resX   = pageWidth - margin - resW;

  doc.setFillColor(240, 250, 249);
  doc.rect(resX, curY, resW, resH, 'F');
  doc.setDrawColor(0, 133, 124);
  doc.rect(resX, curY, resW, resH);
  doc.setDrawColor(0, 0, 0);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('RESUMEN FINANCIERO', resX + resW / 2, curY + 5, { align: 'center' });

  const resItems = [
    ['Saldo Total:',   fmt(resumen.saldo_total  || 0), false],
    ['Saldo Vencido:', fmt(resumen.saldo_vencido || 0), parseFloat(resumen.saldo_vencido || 0) > 0],
  ];
  resItems.forEach(([lbl, val, isRed], idx) => {
    const ry = curY + 5 + (idx + 1) * 6;
    doc.setFont('helvetica', 'bold');
    doc.text(lbl, resX + 4, ry);
    doc.setFont('helvetica', 'normal');
    if (isRed) doc.setTextColor(190, 20, 20);
    doc.text(val, resX + resW - 4, ry, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  });

  if (resumen.proxima_fecha_vencimiento) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Próx. venc.: ${fmt(resumen.proximo_vencimiento || 0)} — ${resumen.proxima_fecha_vencimiento}`,
      resX + resW / 2,
      curY + resH - 3,
      { align: 'center' }
    );
    doc.setTextColor(0, 0, 0);
  }

  // ─── PIE DE PÁGINA (todas las páginas) ────────────────────────────────────
  const totalPgs = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPgs; p++) {
    doc.setPage(p);
    const fy = pageHeight - 8;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, fy - 3, pageWidth - margin, fy - 3);
    doc.text('Universidad LUX | Sistema de Gestión Escolar', margin, fy);
    doc.text(`Página ${p} de ${totalPgs}`, pageWidth - margin, fy, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
  }

  // ─── DESCARGA ─────────────────────────────────────────────────────────────
  const dataUri = doc.output('datauristring');

  if (descargar) {
    const safeNombre = (studentName !== '-' ? studentName : 'alumno')
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_]/g, '')
      .substring(0, 30);
    const safeDate = now.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }).replace(/-/g, '');

    const base64 = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri;
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `estado_cuenta_${safeNombre}_${safeDate}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return dataUri;
}
